import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tag,
  Typography,
} from "@suraasa/placebo-ui"
import {
  attendanceStatus,
  ClassDetailsType,
} from "api/resources/learningItems/types"
import clsx from "clsx"
import CheckRestrictions, {
  getRestriction,
} from "features/AccessManagement/CheckRestrictions"
import { Check, Lock } from "iconoir-react"
import { formatDate } from "utils/helpers"

import styles from "./ClassDetailsCard.module.css"

const ClassDetailsCard = ({
  data,
  onViewRecording,
  flat,
}: {
  data: ClassDetailsType
  onViewRecording: (recordingUrl: string) => void
  /** Drop this card's own border/rounding/background — use when it's
   * rendered as a row inside an already-bordered list. */
  flat?: boolean
}) => {
  const getIcon = (
    item: ClassDetailsType,
    className?: string
  ): JSX.Element | null => {
    if (getRestriction(item)) {
      return (
        <div
          className={clsx(
            "flex size-4 items-center justify-center rounded-full bg-secondary-300 p-0.75 text-onSurface-500",
            className
          )}
        >
          <Lock />
        </div>
      )
    }
    if (item.videoMeeting?.joiningUrl) {
      return (
        <div
          className={clsx(
            "flex size-4 items-center justify-center rounded-full bg-primary-500",
            className
          )}
        >
          <div
            className={clsx(
              "size-1.25 rounded-full bg-white",
              styles.impulseCircle
            )}
          />
        </div>
      )
    }

    switch (item.attendanceStatus) {
      case attendanceStatus.YetToStart:
        return (
          <div
            className={clsx(
              "flex size-4 items-center justify-center rounded-full bg-primary-500",
              className
            )}
          >
            <div className={clsx("size-1.25 rounded-full bg-white")} />
          </div>
        )
      case attendanceStatus.Present:
      case attendanceStatus.RescheduledAndAttended:
        return (
          <div
            className={clsx(
              "flex size-4 items-center justify-center rounded-full bg-success-500 p-0.75 text-white",
              className
            )}
          >
            <Check />
          </div>
        )

      case attendanceStatus.Absent:
      case attendanceStatus.Rescheduled:
      case attendanceStatus.RescheduledButNotAttended:
        return (
          <div
            className={clsx(
              "flex size-4 items-center justify-center rounded-full bg-warning-500",
              className
            )}
          >
            <div className="size-1.25 rounded-full bg-white" />
          </div>
        )
    }

    return (
      <div
        className={clsx(
          "flex size-4 items-center justify-center rounded-full bg-secondary-300 p-0.75 text-onSurface-500",
          className
        )}
      >
        <Lock />
      </div>
    )
  }

  const getAction = () => {
    const joiningUrl = data.videoMeeting?.joiningUrl
    if (joiningUrl) {
      return (
        <CheckRestrictions
          accessStatus={data.accessStatus}
          render={({ isRestricted }) => (
            <Button
              color="black"
              className="w-full"
              disabled={isRestricted}
              variant="filled"
            >
              <a href={joiningUrl} target="_blank" rel="noreferrer">
                Join Class
              </a>
            </Button>
          )}
        />
      )
    }
    if (
      data.learningContentType === "class_schedule" &&
      data.videoMeeting?.recordings &&
      data.videoMeeting.recordings.length > 0
    ) {
      return (
        <DropdownMenu>
          <DropdownMenuTrigger>
            <CheckRestrictions
              accessStatus={data.accessStatus}
              render={({ isRestricted }) => (
                <Button
                  variant="text"
                  className="w-full"
                  disabled={isRestricted}
                >
                  View Recording
                </Button>
              )}
            />
            <DropdownMenuContent>
              {data.videoMeeting.recordings.map((recording, index) => (
                <DropdownMenuItem
                  key={recording.recording}
                  onClick={() => onViewRecording(recording.recording)}
                >
                  Part&nbsp;{index + 1}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenuTrigger>
        </DropdownMenu>
      )
    }
    return null
  }

  const getTag = (
    item: ClassDetailsType,
    className?: string
  ): JSX.Element | null => {
    if (getRestriction(item)) {
      return null
    }
    switch (item.attendanceStatus) {
      case attendanceStatus.Present: {
        return (
          <Tag color="success" className={className}>
            Attended
          </Tag>
        )
      }
      case attendanceStatus.RescheduledAndAttended: {
        return (
          <Tag color="success" className={className}>
            Attended
          </Tag>
        )
      }
      case attendanceStatus.Rescheduled: {
        return (
          <Tag color="warning" className={className}>
            Rescheduled
          </Tag>
        )
      }
      case attendanceStatus.RescheduledButNotAttended: {
        return (
          <Tag color="warning" className={className}>
            Not Attended
          </Tag>
        )
      }
      case attendanceStatus.YetToStart: {
        return item.videoMeeting?.joiningUrl ? null : (
          <Tag color="primary" className={className}>
            Upcoming
          </Tag>
        )
      }
      case attendanceStatus.Absent: {
        return (
          <Tag color="warning" className={className}>
            Not Attended
          </Tag>
        )
      }
      case null:
        return null
    }
  }

  return (
    <div
      className={clsx("flex flex-col p-2 sm:flex-row sm:justify-between", {
        "rounded-2xl border border-surface-200 bg-white": !flat,
      })}
    >
      <div className="flex flex-col sm:flex-row">
        {getIcon(data, "me-0 sm:me-2 mb-2 sm:mb-0 shrink-0")}

        <div>
          <div className="mb-1 flex flex-wrap gap-1 sm:mb-0.5">
            <Typography variant="title3" className="me-1 text-onSurface-800">
              {data.learningContentType === "class"
                ? data?.name
                : data.classId.name}
            </Typography>
            {getTag(data)}
          </div>
          {/* If joiningUrl is present, then class is live */}
          {data.videoMeeting?.joiningUrl ? (
            <div className="mb-1 me-1">
              <Typography
                variant="smallBody"
                className="mb-0.5 text-onSurface-500"
              >
                {formatDate(data.startTime, "do MMMM Y, p")}
              </Typography>
              {data.professor && (
                <Typography variant="smallBody" className="text-onSurface-500">
                  Conducted by Prof. {data.professor.fullName}
                </Typography>
              )}
            </div>
          ) : (
            <>
              <Typography
                variant="smallBody"
                className="mb-1 me-1 text-onSurface-500"
              >
                <>
                  {formatDate(data.startTime, "do MMMM Y, p")}
                  {data.professor && " • Prof. " + data.professor.fullName}
                </>
              </Typography>
            </>
          )}
          {data.lectures && data.lectures.length > 0 && (
            <>
              <Typography className="mb-1" variant="strongSmallBody">
                Topics Covered:
              </Typography>
              <div>
                {data.lectures.map(item => {
                  const tag = item.name.split(":", 1)[0]
                  const name = item.name.split(":", 2)[1]
                  return (
                    <div key={item.id} className="mb-0.5 flex items-center">
                      <Tag color="onSurface" className="me-1">
                        {tag}
                      </Tag>
                      <p className={styles.topicTitle}>{name}</p>
                    </div>
                  )
                })}
              </div>
            </>
          )}
        </div>
      </div>
      <div className="mt-3 sm:mt-0">{getAction()}</div>
    </div>
  )
}

export default ClassDetailsCard
